import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, HStack, Stack, Button, useToast } from "@chakra-ui/react";
import {
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showSuccessToast } from "../../../util/errorHandler";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const Section3 = () => {
  const { alias } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const type = window.location.pathname.split("/")[1];

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "signature",
      "sortable_day",
      "sortable_month",
      "sortable_year",
      "list_of_artists",
      "list_of_artists_alias",
    ];
    const rightFields = [
      "location",
      "medium",
      "number_of_pages",
      "object_of_focus",
      "organising_institution_name",
      "other_artists_in_exhibition",
      "search_text",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      signature: {
        type: "string",
        title: "Signature",
      },
      sortable_day: {
        type: ["number", "null"],
        title: "Sortable day",
        minimum: 1,
      },
      sortable_month: {
        type: ["number", "null"],
        title: "Sortable month",
        minimum: 1,
      },
      sortable_year: {
        type: ["number", "null"],
        title: "Sortable year",
        minimum: 1,
      },
      list_of_artists: {
        type: "string",
        title: "List of artists",
      },
      list_of_artists_alias: {
        type: "string",
        title: "List of artists alias",
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      location: {
        type: "string",
        title: "Location",
      },
      medium: {
        type: "string",
        title: "Medium",
      },
      number_of_pages: {
        type: "string",
        title: "Number of pages",
      },
      object_of_focus: {
        type: "string",
        title: "Object of focus",
      },
      organising_institution_name: {
        type: "string",
        title: "Organising institution name",
      },
      other_artists_in_exhibition: {
        type: "string",
        title: "Other artists in exhibition",
      },
      search_text: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Search Text",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Quadrant-3 fields",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      signature: {
        type: "string",
        title: "Signature",
      },
      sortable_day: {
        type: ["number", "null"],
        title: "Sortable day",
        minimum: 1,
      },
      sortable_month: {
        type: ["number", "null"],
        title: "Sortable month",
        minimum: 1,
      },
      sortable_year: {
        type: ["number", "null"],
        title: "Sortable year",
        minimum: 1,
      },
      list_of_artists: {
        type: "string",
        title: "List of artists",
      },
      list_of_artists_alias: {
        type: "string",
        title: "List of artists alias",
      },
      location: {
        type: "string",
        title: "Location",
      },
      medium: {
        type: "string",
        title: "Medium",
      },
      number_of_pages: {
        type: "string",
        title: "Number of pages",
      },
      object_of_focus: {
        type: "string",
        title: "Object of focus",
      },
      organising_institution_name: {
        type: "string",
        title: "Organising institution name",
      },
      other_artists_in_exhibition: {
        type: "string",
        title: "Other artists in exhibition",
      },
      search_text: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Search Text",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    signature: { "ui:emptyValue": "" },
    sortable_day: { "ui:emptyValue": null },
    sortable_month: { "ui:emptyValue": null },
    sortable_year: { "ui:emptyValue": null },
    list_of_artists: { "ui:emptyValue": "" },
    list_of_artists_alias: { "ui:emptyValue": "" },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    location: { "ui:widget": "hidden" },
    medium: { "ui:emptyValue": "" },
    number_of_pages: { "ui:emptyValue": "" },
    object_of_focus: { "ui:widget": "hidden" },
    organising_institution_name: { "ui:emptyValue": "" },
    other_artists_in_exhibition: { "ui:emptyValue": "" },
    search_text: { "ui:field": HiddenField },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    signature: { "ui:emptyValue": "" },
    sortable_day: { "ui:emptyValue": null },
    sortable_month: { "ui:emptyValue": null },
    sortable_year: { "ui:emptyValue": null },
    list_of_artists: { "ui:emptyValue": "" },
    list_of_artists_alias: { "ui:emptyValue": "" },
    location: { "ui:widget": "hidden" },
    medium: { "ui:emptyValue": "" },
    number_of_pages: { "ui:emptyValue": "" },
    object_of_focus: { "ui:widget": "hidden" },
    organising_institution_name: { "ui:emptyValue": "" },
    other_artists_in_exhibition: { "ui:emptyValue": "" },
    search_text: { "ui:field": HiddenField },
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, "section-3", type);
          if (result) {
            // Process the data to ensure correct types
            const data = result?.data?.[0] || {};

            // Ensure sortable fields have the correct types
            if (
              data.sortable_year !== null &&
              data.sortable_year !== undefined
            ) {
              data.sortable_year = parseInt(data.sortable_year, 10);
            }

            if (
              data.sortable_month !== null &&
              data.sortable_month !== undefined
            ) {
              data.sortable_month = Number(data.sortable_month);
            }

            if (data.sortable_day !== null && data.sortable_day !== undefined) {
              data.sortable_day = Number(data.sortable_day);
            }

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          finalFormData,
          "",
          "section-3",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Library Quadrant-3 Fields
                  </Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Library Quadrant-3 Fields
                      </Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section3;
